import type { SyntheticEvent } from 'react';
import React from 'react';
import HideIcon from '@uber/base-icons/hide-outlined';
import Lock from '@uber/base-icons/lock';
import LockOpen from '@uber/base-icons/lock-open';
import ShowIcon from '@uber/base-icons/show-outlined';
import TrashCan from '@uber/base-icons/trash-can';
import { styled } from 'baseui';
import { Button, KIND as BTN_KIND, SHAPE, SIZE } from 'baseui/button';
import { Checkbox } from 'baseui/checkbox';
import { LOCK_ICON_SIZE, SHOW_HIDE_ICON_SIZE } from '../constant/constant';
import type { AnnotationLabelT, GenericAnnotationT, GetAnnotationMetaDetailsT } from '../types/types';
import AnnotationSubItem from './annotation-subitem';
import { SwatchCircle, Title } from './styled-components';
const LabelContainer = styled('div', ({
  $theme: {
    colors
  }
}) => ({
  color: colors.contentPrimary,
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer'
}));
const LabelContent = styled('div', ({
  $theme: {
    colors,
    sizing
  }
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'background-color 0.2s',
  padding: `${sizing.scale200} ${sizing.scale500}`,
  backgroundColor: colors.backgroundPrimary
}));
const Content = styled('span', {
  display: 'flex',
  alignItems: 'center',
  flexGrow: '1'
});
export const ActionItem = styled('span', {
  display: 'flex',
  alignItems: 'center',
  minWidth: '28px',
  textAlign: 'center'
});
export const LOCK_BUTTON_SELECTOR_ATTRIBUTE_ID = 'lock-button';
export const SHOW_HIDE_BUTTON_SELECTOR_ATTRIBUTE_ID = 'hide-show-button';
export const DELETE_BUTTON_SELECTOR_ATTRIBUTE_ID = 'delete-button';
export const ANNOTATION_ITEM_ATTRIBUTE_ID = 'annotation-list-item';
type ItemProps = {
  editableAnnotationId: string | undefined | null;
  selectedAnnotationIds: Array<string>;
  focusedAnnotationId: string | undefined | null;
  annotationLabels: AnnotationLabelT[];
  onAnnotationSelect: (a: string) => void;
  onAnnotationHover: (a: string) => void;
  onCopyAnnotationId?: (a: string | null) => void;
  onAnnotationDelete?: (a: string) => void;
  onAnnotationEdit?: (annotationId: string) => void;
  onAnnotationLockToggle?: (b: boolean, a: string) => void;
  onAnnotationHideToggle: (b: boolean, a: string) => void;
  onAnnotationCheckedToggle?: (annotationId: string, isChecked: boolean) => void;
  onHideClick: {
    (): void;
  };
  onShowClick: {
    (): void;
  };
  onLockClick: {
    (): void;
  };
  onUnlockClick: {
    (): void;
  };
  checkUpdateAnnotationLabel: (annotationLabel: AnnotationLabelT) => boolean;
  getAnnotationMetaDetails: GetAnnotationMetaDetailsT;
  data: {
    annotations: GenericAnnotationT[];
  } & AnnotationLabelT;
  checkboxDisabledIds?: {
    [x: string]: boolean;
  };
  checkedIds?: {
    [x: string]: boolean;
  };
  onLabelAnnotationsDelete: (a: string) => void;
  handleSelectGroupedAnnotations?: (annotations: GenericAnnotationT[], isSelected: boolean) => void;
  isConsensusPanel?: boolean;
  isGroupSelected?: boolean;
};
export const LockButton = ({
  isLocked,
  isDisabled,
  onLockClick,
  onUnlockClick,
  attributes
}: {
  isLocked: boolean;
  isDisabled?: boolean;
  onLockClick: (e: SyntheticEvent<HTMLButtonElement>) => void;
  onUnlockClick: (e: SyntheticEvent<HTMLButtonElement>) => void;
  attributes?: {
    [name: string]: string;
  };
}) => {
  return <>
      {isLocked ? <Button size={SIZE.mini} onClick={onLockClick} shape={SHAPE.circle} kind={BTN_KIND.tertiary} data-testid="lock-annotation" disabled={isDisabled} {...attributes}>
          <Lock title="Lock annotation" size={LOCK_ICON_SIZE} />
        </Button> : <Button size={SIZE.mini} onClick={onUnlockClick} shape={SHAPE.circle} kind={BTN_KIND.tertiary} data-testid="unlock-annotation" disabled={isDisabled} {...attributes}>
          <LockOpen title="Unlock annotation" size={LOCK_ICON_SIZE} />
        </Button>}
    </>;
};
export const HideButton = ({
  isHidden,
  isDisabled,
  onShowClick,
  onHideClick,
  attributes
}: {
  isHidden: boolean;
  isDisabled?: boolean;
  onShowClick: (e: SyntheticEvent<HTMLButtonElement>) => void;
  onHideClick: (e: SyntheticEvent<HTMLButtonElement>) => void;
  attributes?: {
    [name: string]: string;
  };
}) => {
  return <>
      {isHidden ? <Button size={SIZE.mini} onClick={onShowClick} shape={SHAPE.circle} kind={BTN_KIND.tertiary} disabled={isDisabled} {...attributes}>
          <HideIcon title="Show annotation" size={SHOW_HIDE_ICON_SIZE} />
        </Button> : <Button size={SIZE.mini} onClick={onHideClick} shape={SHAPE.circle} kind={BTN_KIND.tertiary} disabled={isDisabled} {...attributes}>
          <ShowIcon title="Hide annotation" size={SHOW_HIDE_ICON_SIZE} />
        </Button>}
    </>;
};
const AnnotationItem = (props: ItemProps) => {
  const onHideClick = e => {
    e.stopPropagation();
    props.onHideClick();
  };
  const onShowClick = e => {
    e.stopPropagation();
    props.onShowClick();
  };
  const onLockClick = e => {
    e.stopPropagation();
    props.onLockClick();
  };
  const onUnlockClick = e => {
    e.stopPropagation();
    props.onUnlockClick();
  };
  const isLabelLocked = props.data.annotations.some(annotation => annotation.locked);
  const isLabelHidden = props.data.annotations.every(annotation => annotation.hidden);
  return <LabelContainer data-testid={`annotation-label-items-${props.data.title}`}>
      <LabelContent>
        <Content>
          <SwatchCircle $color={props.data.style.fillColor} />
          <Title>{props.data.title}</Title>
        </Content>
        {props.onAnnotationLockToggle && <ActionItem>
            <LockButton isLocked={isLabelLocked} onLockClick={onLockClick} onUnlockClick={onUnlockClick} />
          </ActionItem>}
        <ActionItem>
          <HideButton isHidden={isLabelHidden} onHideClick={onHideClick} onShowClick={onShowClick} />
        </ActionItem>
        <ActionItem>
          {props.isConsensusPanel && props.handleSelectGroupedAnnotations ? <Checkbox checked={props.isGroupSelected || false} onChange={e => {
          props.handleSelectGroupedAnnotations?.(props.data.annotations, e.target.checked);
        }} overrides={{
          Root: {
            style: {
              marginBottom: 0
            }
          },
          Label: {
            style: ({
              $theme
            }) => ({
              fontSize: $theme.typography.LabelXSmall.fontSize
            })
          }
        }} /> : <Button size={SIZE.mini} onClick={() => props.onLabelAnnotationsDelete(props.data.title)} shape={SHAPE.circle} kind={BTN_KIND.tertiary} overrides={{
          BaseButton: {
            style: ({
              $theme
            }) => ({
              color: $theme.colors.negative
            })
          }
        }}>
              <TrashCan title="Delete annotation" size={16} />
            </Button>}
        </ActionItem>
      </LabelContent>
      <div>
        {props.data.annotations.map(item => {
        const {
          id,
          ...annotation
        } = item;
        const isEditable = props.editableAnnotationId === id;
        const isSelected = props.selectedAnnotationIds.indexOf(id) >= 0;
        const isFocused = props.focusedAnnotationId === id && !isEditable;
        const isLocked = annotation.locked;
        return <AnnotationSubItem key={id} id={id}
        // @ts-expect-error todo(ts-migration) TS2741 Property 'id' is missing in type '{ annotationLabel: AnnotationLabelT; metadata?: { answers?: AnswerT; }; annotationType: string; hidden?: boolean; }' but required in type 'GenericAnnotationT'.
        annotation={annotation} isEditable={isEditable} isSelected={isSelected} isFocused={isFocused} isLocked={isLocked} onAnnotationDelete={props.onAnnotationDelete} onCopyAnnotationId={props.onCopyAnnotationId} onAnnotationHideToggle={props.onAnnotationHideToggle} onAnnotationSelect={props.onAnnotationSelect} onAnnotationHover={props.onAnnotationHover}
        // Temporarily removing the edit icon for segmentation annotations, as updating the label using the edit icon
        // doesn't work currently due to state updates not triggering segmentation re-renders.
        onAnnotationEdit={props.onAnnotationEdit} getAnnotationMetaDetails={props.getAnnotationMetaDetails} onAnnotationCheckedToggle={props.onAnnotationCheckedToggle} isCheckboxDisabled={props.checkboxDisabledIds?.[id]} isChecked={props.checkedIds?.[id]} {...props.onAnnotationLockToggle && {
          onAnnotationLockToggle: props.onAnnotationLockToggle
        }} />;
      })}
      </div>
    </LabelContainer>;
};
export default AnnotationItem;